/**
 * index.js — сборка плеера: связывает тракт (engine), фонотеку (library),
 * картинку (visual, peaks) и разметку страницы.
 *
 * Раскладка ответственности намеренная: движок не знает слова «трек»,
 * фонотека не знает слова «звук», а всё, что знает про DOM, живёт только тут.
 *
 * ПЕРЕХОД МЕЖДУ ТРЕКАМИ. Прежний плеер менял src у одного элемента — это
 * пауза в четверть секунды на каждом стыке, и живой альбом рассыпается.
 * Здесь два элемента: следующий трек загружается за двенадцать секунд до
 * конца текущего и стартует за 20 мс до его окончания. Формулировка в
 * интерфейсе — «без паузы между треками», а не «gapless по семплам»: точность
 * здесь порядка десятка миллисекунд, и обещать больше было бы неправдой.
 */

import { PlayerEngine } from './engine.js';
import { Library } from './library.js';
import { Spectrum, Meters } from './visual.js';
import { computePeaks, drawPeaks } from './peaks.js';
import { readMetadata } from './metadata.js';
import { knownGain, scan, scannable, TARGET_LUFS } from './replaygain.js';
import { BAND_SETS, PRESETS, presetGains, curveAt } from './eq.js';
import { parseEqText, exportEqText, makeFilter, MAX_FILTERS } from './parametric.js';
import { readCueFile, matchAudio } from './cue.js';
import * as Store from './library-store.js';
import * as Exotic from './exotic.js';

const $ = (id) => document.getElementById(id);
const SET = 'audioplayer';
const PRELOAD_AHEAD = 12;
const HANDOFF_LEAD = 0.02;

const T = window.__MP_T || {};
const t = (k, sub) => {
    let s = T[k] != null ? T[k] : k;
    /* ⚠️ Длинные имена подставляются ПЕРВЫМИ. Иначе `:n` съедает начало
       `:name`, и строка «Загружено фильтров: :n. :name» выходит на экран как
       «Загружено фильтров: 6. 6ame» — замерено 21.08 на живой странице.
       Порядок Object.entries тут ни при чём: виновата длина ключа. */
    if (sub) {
        for (const a of Object.keys(sub).sort((x, y) => y.length - x.length)) {
            s = s.split(':' + a).join(sub[a]);
        }
    }
    return s;
};

const store = {
    load: (k, d) => { try { return window.__toolSettings.load(SET, k, d); } catch (_) { return d; } },
    save: (k, v) => { try { window.__toolSettings.save(SET, k, v); } catch (_) {} },
};

const fmtTime = (s) => {
    s = Math.max(0, Math.floor(s || 0));
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), x = s % 60;
    return (h ? h + ':' + String(m).padStart(2, '0') : String(m)) + ':' + String(x).padStart(2, '0');
};
const NOTE_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>';
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

class Player {
    constructor() {
        this.eng = new PlayerEngine();
        this.lib = new Library();
        this.spectrum = null;
        this.meters = null;
        this.playing = false;
        this.pendingNextId = null;
        this.handoffTimer = 0;
        this.crossfade = 0;
        this.stopAfterCurrent = false;
        this.sleepAt = 0;
        this.sleepTimer = 0;
        this.peaksAbort = null;
        this.curPeaks = null;
        this.rowH = 46;
        this.blobUrls = new Map();
        this.scanQueue = [];
        this.scanning = false;
        this.playedFrom = 0;
        this.savedIndex = null;
        this.folderName = '';
        this._persistTimer = 0;
    }

    /* ── запуск ───────────────────────────────────────────────────────── */

    async boot() {
        this.cacheDom();
        this.restore();
        this.bindUi();
        this.bindKeys();
        this.syncEmptyState();
        this.renderList();
        this.renderNow();
        this.offerSavedLibrary();
    }

    /**
     * Показать, что было в прошлый раз. Только ПОКАЗАТЬ: доступ к файлам
     * браузер отдаёт лишь по нажатию, и спрашивать его на загрузке страницы
     * бесполезно — окно не появится, а ответ навсегда останется «спросить».
     */
    async offerSavedLibrary() {
        if (!Store.canPersist()) return;
        let idx = null;
        try { idx = await Store.loadIndex(); } catch (_) {}
        if (!idx || !idx.tracks || !idx.tracks.length) return;
        this.savedIndex = idx;
        const bar = this.el.saved;
        if (!bar) return;
        this.el.savedName.textContent = idx.name;
        this.el.savedCount.textContent = t('count', { n: idx.tracks.length, time: fmtTime(idx.tracks.reduce((a, x) => a + (x.duration || 0), 0)) });
        bar.hidden = false;
    }

    /** Открыть запомненную папку. Вызывается ИЗ НАЖАТИЯ — иначе разрешения не будет. */
    async reopenSaved() {
        const r = await Store.reopen();
        if (!r) { this.el.saved.hidden = true; return; }
        if (r.denied) { window.__toast && window.__toast(t('lib_denied'), 'warning'); return; }
        this.el.saved.hidden = true;
        await this.addFiles(r.files, { folder: r.name });
    }

    cacheDom() {
        this.el = {
            app: $('mpApp'),
            cover: $('mpCover'), coverImg: $('mpCoverImg'),
            title: $('mpTitle'), sub: $('mpSub'), tech: $('mpTech'),
            cur: $('mpCur'), dur: $('mpDur'),
            wave: $('mpWave'), waveWrap: $('mpWaveWrap'), seek: $('mpSeek'),
            play: $('mpPlay'), playIcon: $('mpPlayIcon'),
            prev: $('mpPrev'), next: $('mpNext'), back: $('mpBack'), fwd: $('mpFwd'),
            shuffle: $('mpShuffle'), repeat: $('mpRepeat'),
            vol: $('mpVol'), mute: $('mpMute'), rate: $('mpRate'),
            spectrum: $('mpSpectrum'), metersRoot: $('mpMeters'),
            list: $('mpList'), listBody: $('mpListBody'), empty: $('mpEmpty'), count: $('mpCount'),
            search: $('mpSearch'),
            eqPanel: $('mpEqPanel'), eqBands: $('mpEqBands'), eqPreset: $('mpEqPreset'),
            eqOn: $('mpEqOn'), eqBandCount: $('mpEqBandCount'), eqPreamp: $('mpEqPreamp'),
            eqImport: $('mpEqImport'), eqExport: $('mpEqExport'), eqFile: $('mpEqFile'),
            param: $('mpParam'), paramRows: $('mpParamRows'), paramAdd: $('mpParamAdd'),
            paramNote: $('mpParamNote'),
            eqPreampVal: $('mpEqPreampVal'), eqAuto: $('mpEqAuto'), eqReset: $('mpEqReset'),
            rgMode: $('mpRgMode'), rgScan: $('mpRgScan'), rgStatus: $('mpRgStatus'),
            limiter: $('mpLimiter'), device: $('mpDevice'), deviceWrap: $('mpDeviceWrap'),
            crossfade: $('mpCrossfade'), crossfadeVal: $('mpCrossfadeVal'), crossfeed: $('mpCrossfeed'),
            convPick: $('mpConvPick'), convInput: $('mpConvInput'), convOn: $('mpConvOn'),
            convOnWrap: $('mpConvOnWrap'), convClear: $('mpConvClear'), convInfo: $('mpConvInfo'),
            sleep: $('mpSleep'), sleepLabel: $('mpSleepLabel'), sleepPop: $('mpSleepPop'),
            stopAfter: $('mpStopAfter'),
            queueBadge: $('mpQueueBadge'),
            hero: $('mpHero'), deck: $('mpDeck'), listPanel: $('mpListPanel'),
            saved: $('mpSaved'), savedName: $('mpSavedName'), savedCount: $('mpSavedCount'),
            savedOpen: $('mpSavedOpen'), savedForget: $('mpSavedForget'),
        };
    }

    restore() {
        this.lib.repeat = store.load('repeat', 'off');
        this.lib.setShuffle(store.load('shuffle', false));
        this.crossfade = +store.load('crossfade', 0) || 0;
        // 'param' — свободный набор фильтров; иначе число полос.
        const savedBands = store.load('bands', 10);
        this.savedEqMode = savedBands === 'param' ? 'parametric' : 'graphic';
        const bands = +savedBands === 31 ? 31 : 10;
        this.savedBands = bands;
        this.savedFilters = (store.load('filters', []) || []).slice(0, 32);
        this.savedGains = store.load('gains:' + bands, new Array(BAND_SETS[bands].freqs.length).fill(0));
        this.savedEqOn = !!store.load('eqOn', false);
        this.savedPreset = store.load('preset', 'flat');
        this.savedRg = store.load('rg', 'off');
        this.savedLimiter = store.load('limiter', true) !== false;
        this.savedVol = Math.min(1, Math.max(0, +store.load('volume', 1)));
        this.savedRate = +store.load('rate', 1) || 1;
        this.savedCrossfeed = store.load('crossfeed', 'off');
    }

    /** Звук поднимается ТОЛЬКО по жесту: до него страница молчит и ничего не грузит. */
    async ensureEngine() {
        if (this.eng.ready) { await this.eng.resume(); return; }
        await this.eng.init(window.__MP_WORKLETS || []);
        this.eng.onMeter = (m) => this.meters && this.meters.update(m);
        this.eng.setBandCount(this.savedBands);
        this.eng.setEqGains(this.savedGains);
        if (this.savedFilters.length) this.eng.setFilters(this.savedFilters);
        if (this.savedEqMode === 'parametric') this.eng.setEqMode('parametric');
        this.eng.setEqEnabled(this.savedEqOn);
        this.eng.setLimiter(this.savedLimiter);
        this.eng.setRgMode(this.savedRg);
        this.eng.setCrossfeed(this.savedCrossfeed);
        this.eng.setVolume(this.savedVol);
        for (const el of this.eng.els) {
            el.addEventListener('timeupdate', () => this.onTime());
            el.addEventListener('ended', () => this.onEnded(el));
            el.addEventListener('error', () => this.onError(el));
        }
        this.spectrum = new Spectrum(this.el.spectrum, this.eng);
        this.meters = new Meters(this.el.metersRoot);
        this.buildEqSliders();
        await this.eng.resume();
        this.fillDevices();
    }

    /* ── файлы ────────────────────────────────────────────────────────── */

    async addFiles(files, opts = {}) {
        const audio = [];
        const m3u = [];
        const cues = [];
        for (const f of files) {
            if (/\.cue$/i.test(f.name)) { cues.push(f); continue; }
            if (/\.(m3u8?|pls)$/i.test(f.name)) { m3u.push(f); continue; }
            if (f.type.startsWith('audio/') || f.type.startsWith('video/')
                || /\.(mp3|wav|flac|ogg|oga|opus|aac|m4a|m4b|mp4|weba|webm|wma|aif{1,2}|caf|3gp|amr|ape|wv|mka|mid)$/i.test(f.name)) {
                audio.push(f);
            }
        }
        if (!audio.length && !m3u.length && !cues.length) return;

        /* CUE-лист превращает ОДИН длинный файл в список треков — то, чем
           Левону нравится AIMP («аимп хорошо разделяет на треки»). Файл,
           попавший под разметку, в список отдельной дорожкой не идёт: иначе
           альбом появился бы дважды — разобранным и целиком. */
        const claimed = new Set();
        let added = [];
        for (const cf of cues) {
            const cue = await readCueFile(cf);
            if (!cue || !cue.tracks.length) continue;
            const src = matchAudio(cue, audio);
            if (!src) { window.__toast && window.__toast(t('cue_no_audio', { name: cue.file || cf.name }), 'warning'); continue; }
            claimed.add(src);
            added = added.concat(this.lib.addCue(src, cue));
            window.__toast && window.__toast(t('cue_loaded', { n: cue.tracks.length }), 'success');
        }
        added = added.concat(this.lib.add(audio.filter(f => !claimed.has(f))));

        /* Что уже было измерено — не меряем заново. Сопоставление по ПУТИ, а
           не по порядку обхода: файловая система порядок не гарантирует, и
           опора на него перепутала бы треки местами. */
        if (this.savedIndex) {
            const byPath = new Map(this.savedIndex.tracks.map(x => [x.path, x]));
            for (const tr of added) {
                const key = tr.file.__path || tr.file.name;
                const old = byPath.get(key);
                if (!old) continue;
                if (!tr.duration && old.duration) tr.duration = old.duration;
                if (tr.lufs == null && old.lufs != null) { tr.lufs = old.lufs; tr.rgSource = old.rgSource; }
                if (!tr.meta && old.meta) tr.meta = Object.assign({ rg: { trackGainDb: null, albumGainDb: null, trackPeak: null, albumPeak: null } }, old.meta);
            }
        }
        if (opts.folder) this.folderName = opts.folder;
        this.renderList();
        this.updateCount();

        if (m3u.length) {
            try {
                const names = Library.parseM3U(await m3u[0].text());
                this.lib.applyM3UOrder(names);
                this.renderList();
            } catch (_) {}
        }

        /* Экзотический формат в списке — повод НАЧАТЬ качать ядро ffmpeg
           сейчас, а не в момент отказа воспроизведения: у него свой потолок
           ожидания в 15 секунд, и на медленной сети «файл не читается»
           означало бы всего лишь «ядро не успело». */
        if (added.some(x => Exotic.worthTrying(x.file))) { try { Exotic.warmUp(); } catch (_) {} }

        // Метаданные и длительности — порциями, чтобы список не подвисал.
        this.hydrate(added);
        if (this.lib.currentId == null && added.length) this.markReady(added[0]);
    }

    async hydrate(tracks) {
        const chunk = 4;
        for (let i = 0; i < tracks.length; i += chunk) {
            const part = tracks.slice(i, i + chunk);
            await Promise.all(part.map(async (tr) => {
                if (tr.cue) {
                    // Заголовки уже пришли из разметки; из файла нужны только
                    // формат и обложка — и то один раз на весь альбом.
                    try {
                        const m = await readMetadata(tr.file);
                        if (m.coverUrl && !tr.cover) tr.cover = m.coverUrl;
                        tr.meta.format = m.format || 'cue';
                    } catch (_) {}
                    try {
                        const h = window.AudioHeaderProbe ? await window.AudioHeaderProbe.probe(tr.file) : null;
                        if (h && h.sampleRate) tr.fmt = h;
                    } catch (_) {}
                    if (!tr.duration) {
                        const total = await this.probeDuration(tr.file);
                        tr.duration = Math.max(0, (tr.cue.end != null ? tr.cue.end : total) - tr.cue.start);
                    }
                    return;
                }
                try {
                    tr.meta = await readMetadata(tr.file);
                    if (tr.meta.coverUrl) tr.cover = tr.meta.coverUrl;
                    const g = knownGain(tr, 'track');
                    if (g) { tr.rgSource = g.source; if (g.lufs != null) tr.lufs = g.lufs; }
                } catch (_) {}
                // Частоту и разрядность берём из ЗАГОЛОВКА файла, а не из
                // аудиоконтекста: контекст всегда отдаёт частоту устройства
                // (обычно 48 кГц), и запись 44.1/16 выглядела бы как 48.
                // Тому, кто сверяет мастер с эталоном, это прямая дезинформация.
                try {
                    const h = window.AudioHeaderProbe ? await window.AudioHeaderProbe.probe(tr.file) : null;
                    if (h && h.sampleRate) tr.fmt = h;
                } catch (_) {}
                tr.duration = await this.probeDuration(tr.file);
            }));
            this.renderList();
            if (this.lib.currentId != null) this.renderNow();
            await new Promise(r => setTimeout(r, 0));
        }
        this.lib.reindex();
        this.renderList();
        this.persistIndex();
    }

    /** Обновить указатель фонотеки. Тихо: это удобство, а не работа тула. */
    persistIndex() {
        if (!Store.canPersist() || !this.folderName) return;
        clearTimeout(this._persistTimer);
        this._persistTimer = setTimeout(() => {
            Store.saveIndex(this.folderName, this.lib.tracks).catch(() => {});
        }, 1200);
    }

    probeDuration(file) {
        return new Promise((res) => {
            const url = URL.createObjectURL(file);
            const a = new Audio();
            a.preload = 'metadata';
            const done = (v) => { URL.revokeObjectURL(url); a.removeAttribute('src'); res(v); };
            a.addEventListener('loadedmetadata', () => done(isFinite(a.duration) ? a.duration : 0));
            a.addEventListener('error', () => done(0));
            setTimeout(() => done(0), 8000);
            a.src = url;
        });
    }

    markReady(track) { this.lib.setCurrent(track.id); this.renderNow(); this.renderList(); }

    urlFor(track) {
        // Развёрнутый ffmpeg'ом WAV заменяет исходник целиком: в нём УЖЕ
        // вырезан нужный отрезок, поэтому границы CUE к нему не применяются
        // (см. bounds()).
        if (track.exoticUrl) return track.exoticUrl;
        if (!this.blobUrls.has(track.id)) this.blobUrls.set(track.id, URL.createObjectURL(track.file));
        return this.blobUrls.get(track.id);
    }

    /* ── воспроизведение ──────────────────────────────────────────────── */

    /**
     * play() плеера. Отказ ИСТОЧНИКА (NotSupportedError) разбирает сам плеер:
     * следом элемент поднимет `error`, и onError отдаст файл ffmpeg. Сказать
     * «браузер не может воспроизвести» раньше этой попытки — значит объявить
     * поражение там, где через секунду заиграет музыка, поэтому промис
     * помечается своим (`__tmbPlayOwned` — так ядро понимает, что отказ уже
     * разобран). Прочие отказы (нет жеста, занято устройство) — ядру, как и
     * раньше: там его фраза верная.
     */
    playEl(el) {
        let p;
        try { p = el.play(); } catch (e) { return Promise.reject(e); }
        if (!p || typeof p.then !== 'function') return Promise.resolve();
        try { p.__tmbPlayOwned = true; } catch (_) {}
        return p.catch((e) => {
            if (!(e && e.name === 'NotSupportedError') && typeof window.__notePlayFailure === 'function') {
                try { window.__notePlayFailure(el, e, true); } catch (_) {}
            }
            throw e;
        });
    }

    async playTrack(id, { fromStart = true } = {}) {
        await this.ensureEngine();
        const tr = this.lib.get(id);
        if (!tr) return;

        this.finishLoudnessWatch();
        this.cancelHandoff();
        this.lib.dequeue(id);

        const slot = this.eng.active;
        const url = this.urlFor(tr);
        const el = this.eng.el(slot);
        /* Соседний отрезок ТОГО ЖЕ файла не перезагружаем: элемент уже играет
           нужные байты, менять надо только границы. Это и даёт внутри альбома
           переход без единого пропущенного семпла — не «почти без паузы», а
           буквально продолжение того же потока. */
        /* Ключ включает развёрнутый URL. Без него после неудачной попытки
           сыграть .ape ключ оставался прежним («тот же файл»), элемент не
           перезаряжался, и развёрнутый WAV никогда не доходил до звука:
           формат разворачивался успешно и молча выбрасывался. */
        const srcKey = tr.exoticUrl || (tr.file.name + '|' + tr.file.size);
        const sameSource = el.getAttribute('data-src-key') === String(srcKey);
        if (!sameSource) {
            this.eng.load(slot, url);
            el.setAttribute('data-src-key', String(srcKey));
        }
        const seekTo = tr.cue ? tr.cue.start : 0;
        if (fromStart || tr.cue) {
            const applySeek = () => { try { el.currentTime = seekTo; } catch (_) {} };
            if (el.readyState >= 1) applySeek();
            else el.addEventListener('loadedmetadata', applySeek, { once: true });
        }
        el.playbackRate = this.savedRate;
        this.applyGain(slot, tr);

        this.lib.setCurrent(id);
        this.playedFrom = 0;
        this.eng.beginLoudnessWatch(tr.file);

        try { await this.playEl(el); this.playing = true; }
        catch (e) {
            this.playing = false;
            // Отказ источника — забота onError (ffmpeg); прочее ядро уже сказало.
            if (!(e && e.name === 'NotSupportedError')) window.__handleError && window.__handleError(e, 'audio-player');
        }

        this.renderNow();
        this.renderList();
        this.updatePlayIcon();
        this.spectrum && this.spectrum.start();
        this.meters && this.meters.reset();
        this.loadPeaks(tr);
        this.mediaSession(tr);
        this.ensureGain(tr);
        try { window.__aeFunnel && window.__aeFunnel('play', { fmt: tr.ext }); } catch (_) {}
    }

    applyGain(slot, tr) {
        const g = this.eng.applyTrackGain(slot, tr);
        if (this.el.rgStatus) {
            this.el.rgStatus.textContent = this.eng.rgMode === 'off' ? t('rg_off')
                : g ? t('rg_applied', { db: (g.gainDb >= 0 ? '+' : '') + g.gainDb.toFixed(1), src: t('rg_src_' + g.source) })
                    : t('rg_unknown');
        }
    }

    /** Если громкость трека неизвестна, а выравнивание включено — измерить. */
    ensureGain(tr) {
        if (this.eng.rgMode === 'off') return;
        if (knownGain(tr, this.eng.rgMode)) return;
        if (!this.scanQueue.includes(tr.id)) this.scanQueue.push(tr.id);
        this.drainScanQueue();
    }

    async drainScanQueue() {
        if (this.scanning) return;
        this.scanning = true;
        while (this.scanQueue.length) {
            const id = this.scanQueue.shift();
            const tr = this.lib.get(id);
            if (!tr) continue;
            try {
                const fit = await scannable(tr.file);
                if (!fit.ok) { tr.rgSource = 'toobig'; continue; }
                const r = await scan(tr.file);
                if (r) {
                    tr.lufs = r.lufs;
                    tr.rgSource = 'scan';
                    this.persistIndex();
                    if (this.lib.currentId === id) this.applyGain(this.eng.active, tr);
                    this.renderList();
                }
            } catch (_) {}
            await new Promise(r => setTimeout(r, 30));
        }
        this.scanning = false;
    }

    async togglePlay() {
        await this.ensureEngine();
        if (!this.lib.tracks.length) return;
        if (this.lib.currentId == null) { const v = this.lib.view(); if (v.length) return this.playTrack(v[0].id); return; }
        const el = this.eng.el();
        // Трек может быть ВЫБРАН, но ещё не заряжен: после добавления файлов
        // первый становится текущим, чтобы его было видно в шапке, а байты в
        // элемент не грузятся — до жеста страница молчит. Без этой ветки первое
        // же нажатие на «Слушать» не делало ничего: play() на элементе без src.
        if (!el.src) return this.playTrack(this.lib.currentId);
        if (el.paused) {
            try { await this.playEl(el); this.playing = true; this.spectrum && this.spectrum.start(); } catch (_) {}
        } else { el.pause(); this.playing = false; this.spectrum && this.spectrum.stop(); }
        this.updatePlayIcon();
        this.mediaSession(this.lib.current);
    }

    async next(manual) {
        const id = this.lib.next();
        if (id == null) { this.stop(); return; }
        await this.playTrack(id);
        if (manual) try { window.__aeFunnel && window.__aeFunnel('skip_next'); } catch (_) {}
    }

    async prev() {
        const el = this.eng.ready ? this.eng.el() : null;
        // Как у всех плееров: первое нажатие возвращает к началу трека.
        if (el && el.currentTime > 3) { el.currentTime = 0; return; }
        const id = this.lib.prev();
        if (id != null) await this.playTrack(id);
    }

    stop() {
        if (this.eng.ready) { for (const el of this.eng.els) { try { el.pause(); } catch (_) {} } }
        this.playing = false;
        this.cancelHandoff();
        this.finishLoudnessWatch();
        this.updatePlayIcon();
        this.spectrum && this.spectrum.stop();
        this.meters && this.meters.reset();
    }

    /** Границы того, что сейчас считается «треком»: файл целиком или отрезок CUE. */
    bounds() {
        const el = this.eng.el();
        const fileDur = isFinite(el.duration) ? el.duration : 0;
        const tr = this.lib.current;
        if (tr && tr.exoticUrl) return { start: 0, end: fileDur, dur: fileDur };
        if (tr && tr.cue) {
            const start = tr.cue.start;
            const end = tr.cue.end != null ? tr.cue.end : fileDur;
            return { start, end, dur: Math.max(0, end - start) };
        }
        return { start: 0, end: fileDur, dur: fileDur };
    }

    seekTo(frac) {
        if (!this.eng.ready) return;
        const el = this.eng.el();
        const b = this.bounds();
        if (!b.dur) return;
        el.currentTime = Math.max(b.start, Math.min(b.end - 0.05, b.start + frac * b.dur));
        this.onTime();
    }

    /* ── стык треков ──────────────────────────────────────────────────── */

    onTime() {
        if (!this.eng.ready) return;
        const el = this.eng.el();
        const b = this.bounds();
        const pos = Math.max(0, el.currentTime - b.start);
        this.playedFrom = pos;
        if (this.el.cur) this.el.cur.textContent = fmtTime(pos);
        if (this.el.dur) this.el.dur.textContent = b.dur ? fmtTime(b.dur) : '--:--';
        const frac = b.dur ? pos / b.dur : 0;
        if (this.el.seek) this.el.seek.style.width = (frac * 100).toFixed(3) + '%';
        if (this.el.waveWrap) this.el.waveWrap.setAttribute('aria-valuenow', Math.round(frac * 100));
        if (this.curPeaks) this.drawWave(frac);

        if (!b.dur || el.paused) return;

        // Конец ОТРЕЗКА наступает раньше конца файла — событие ended сюда не
        // придёт, границу держим сами.
        const cur = this.lib.current;
        if (cur && cur.cue && el.currentTime >= b.end - 0.03) { this.advanceFromCue(); return; }

        const left = b.end - el.currentTime;
        if (left <= PRELOAD_AHEAD) this.preloadNext();
        const at = Math.max(HANDOFF_LEAD, this.crossfade);
        if (left <= at && !this.handoffTimer) this.startHandoff(left, at);
    }

    /**
     * Переход через границу отрезка CUE. Если следующий трек — соседний
     * отрезок того же файла, меняется ТОЛЬКО текущий номер: звук не
     * прерывается ни на семпл. Иначе — обычный переход.
     */
    advanceFromCue() {
        if (this.stopAfterCurrent) { this.stopAfterCurrent = false; this.syncStopAfter(); this.stop(); return; }
        if (this.lib.repeat === 'one') { this.seekTo(0); return; }
        const nextId = this.lib.next(true);
        if (nextId == null) { this.stop(); return; }
        if (this.lib.isContiguousNext(this.lib.currentId, nextId) && !this.crossfade) {
            this.lib.next();                       // снять из очереди, если он оттуда
            this.finishLoudnessWatch();
            const tr = this.lib.get(nextId);
            this.lib.setCurrent(nextId);
            this.applyGain(this.eng.active, tr);
            this.eng.beginLoudnessWatch(tr.file);
            this.renderNow(); this.renderList(); this.mediaSession(tr); this.ensureGain(tr);
            return;
        }
        this.next();
    }

    preloadNext() {
        if (this.pendingNextId != null || this.stopAfterCurrent) return;
        const id = this.lib.next(true);
        if (id == null || id === this.lib.currentId) return;
        const tr = this.lib.get(id);
        if (!tr) return;
        this.pendingNextId = id;
        const slot = this.eng.active ? 0 : 1;
        this.eng.load(slot, this.urlFor(tr));
        this.eng.el(slot).playbackRate = this.savedRate;
    }

    startHandoff(left, at) {
        if (this.pendingNextId == null) return;
        const delay = Math.max(0, (left - at) * 1000);
        this.handoffTimer = setTimeout(() => this.doHandoff(), delay);
    }

    cancelHandoff() {
        if (this.handoffTimer) clearTimeout(this.handoffTimer);
        this.handoffTimer = 0;
        this.pendingNextId = null;
    }

    doHandoff() {
        this.handoffTimer = 0;
        const id = this.pendingNextId;
        this.pendingNextId = null;
        if (id == null || this.stopAfterCurrent) return;
        const tr = this.lib.get(id);
        if (!tr) return;

        const oldSlot = this.eng.active;
        const newSlot = this.eng.swapSlots();
        const nel = this.eng.el(newSlot);
        this.finishLoudnessWatch();
        this.applyGain(newSlot, tr);
        try { nel.currentTime = 0; } catch (_) {}
        this.playEl(nel).catch(() => {});

        if (this.crossfade > 0) {
            // Равномощный переход: сумма квадратов усилений постоянна, иначе
            // на стыке проваливается громкость.
            const ctx = this.eng.ctx, now = ctx.currentTime, d = this.crossfade;
            const target = this.eng.rg[newSlot].gain.value;
            this.eng.rg[newSlot].gain.cancelScheduledValues(now);
            this.eng.rg[newSlot].gain.setValueAtTime(0.0001, now);
            this.eng.rg[newSlot].gain.exponentialRampToValueAtTime(Math.max(0.0002, target), now + d);
            const cur = this.eng.rg[oldSlot].gain.value;
            this.eng.rg[oldSlot].gain.cancelScheduledValues(now);
            this.eng.rg[oldSlot].gain.setValueAtTime(Math.max(0.0002, cur), now);
            this.eng.rg[oldSlot].gain.exponentialRampToValueAtTime(0.0001, now + d);
            setTimeout(() => { try { this.eng.el(oldSlot).pause(); } catch (_) {} }, d * 1000 + 120);
        }

        this.lib.setCurrent(id);
        this.eng.beginLoudnessWatch(tr.file);
        this.renderNow(); this.renderList();
        this.loadPeaks(tr); this.mediaSession(tr); this.ensureGain(tr);
    }

    onEnded(el) {
        if (this.eng.el() !== el) return;              // доигрывал предыдущий при кроссфейде
        if (this.stopAfterCurrent) { this.stopAfterCurrent = false; this.syncStopAfter(); this.stop(); return; }
        if (this.lib.repeat === 'one') { el.currentTime = 0; this.playEl(el).catch(() => {}); return; }
        if (this.pendingNextId != null) { this.doHandoff(); return; }
        this.next();
    }

    onError(el) {
        if (this.eng.el() !== el) return;
        const tr = this.lib.current;
        if (!tr) return;
        /* Отказ элемента — это и есть сигнал попробовать ffmpeg. Не список
           расширений: списки протухают в обе стороны, и угадывание либо
           будит 32 МБ ffmpeg зря, либо молчит там, где он нужен. */
        if (!tr.exoticTried && !tr.exoticUrl && Exotic.available() && Exotic.rescuable(tr.file)) {
            tr.exoticTried = true;
            this.unpack(tr);
            return;
        }
        tr.error = t('err_decode');
        this.renderList();
        window.__toast && window.__toast(t('err_decode'), 'error');
        this.next();
    }

    /** Развернуть формат, которого браузер не знает, и продолжить с того же места. */
    async unpack(tr) {
        const label = (tr.meta && tr.meta.title) || tr.name;
        /* Экзотика (APE, WavPack…) — «формат браузер не знает» правда. А .mp3
           или .wav, на котором браузер споткнулся, — файл ЗНАКОМОГО формата с
           незнакомым содержимым (ADPCM, ALAC, WMA под чужим именем): сказать
           «MP3 браузер не знает» было бы неправдой. */
        const rescue = !Exotic.worthTrying(tr.file);
        const t0 = performance.now();
        window.__toast && window.__toast(rescue ? t('rescue_start', { name: label })
            : t('exotic_start', { name: label, fmt: tr.ext.toUpperCase() }), 'info');
        this.el.tech.textContent = t('exotic_progress', { p: '0' });
        const r = await Exotic.toWav(tr.file, tr.cue, {
            hdr: tr.fmt,
            durationSec: tr.duration,
            onProgress: (p) => { this.el.tech.textContent = t('exotic_progress', { p: String(Math.round(p * 100)) }); },
        });
        const report = (ok, why) => {
            try { window.__ae && window.__ae('feature_use', { slug: 'audio-player', f: 'unplayable_rescue', fmt: tr.ext, rescue: rescue ? 1 : 0, ok: ok ? 1 : 0, why: why || '', mb: Math.round((tr.file.size || 0) / 1048576), ms: Math.round(performance.now() - t0) }); } catch (_) {}
        };
        if (!r || r.tooBig) {
            // Причину кладём НА ТРЕК: модуль грузится по версионированному
            // пути, и его lastError() из другого экземпляра не виден ни
            // стенду, ни отладке.
            tr.exoticErr = r && r.tooBig ? 'too_big' : (Exotic.lastError() || 'unpack_returned_null');
            tr.error = r && r.tooBig ? t('exotic_too_big', { mb: String(r.needMb) }) : t('rescue_failed', { name: label });
            report(false, tr.exoticErr.slice(0, 80));
            this.renderList();
            window.__toast && window.__toast(tr.error, 'warning');
            this.renderNow();
            return;
        }
        tr.exoticUrl = r.url;
        report(true);
        try { window.__aeFunnel && window.__aeFunnel('exotic_decoded', { fmt: tr.ext, cue: tr.cue ? 1 : 0, rescue: rescue ? 1 : 0 }); } catch (_) {}
        await this.playTrack(tr.id);
    }

    finishLoudnessWatch() {
        if (!this.eng.ready) return;
        const tr = this.lib.current;
        const lufs = this.eng.endLoudnessWatch(this.playedFrom, tr ? tr.duration : 0);
        if (lufs != null && tr) { tr.lufs = lufs; tr.rgSource = 'listen'; this.renderList(); }
    }

    /* ── картинка ─────────────────────────────────────────────────────── */

    async loadPeaks(tr) {
        this.curPeaks = null;
        this.drawWave(0);
        if (this.peaksAbort) this.peaksAbort.abort();
        this.peaksAbort = new AbortController();
        const sig = this.peaksAbort.signal;
        const p = await computePeaks(tr.file, sig);
        if (sig.aborted || this.lib.currentId !== tr.id) return;
        // У отрезка CUE своя часть общей огибающей: волна должна показывать
        // трек, а не весь альбом.
        this.curPeaks = (p && tr.cue) ? this.slicePeaks(p, tr) : p;
        const b = this.bounds();
        this.drawWave(b.dur ? Math.max(0, this.eng.el().currentTime - b.start) / b.dur : 0);
    }

    /** Часть огибающей файла, приходящаяся на отрезок CUE. */
    slicePeaks(peaks, tr) {
        const el = this.eng.ready ? this.eng.el() : null;
        const total = el && isFinite(el.duration) ? el.duration : 0;
        if (!total) return peaks;
        const from = Math.floor((tr.cue.start / total) * peaks.length);
        const to = Math.ceil(((tr.cue.end != null ? tr.cue.end : total) / total) * peaks.length);
        return peaks.subarray(Math.max(0, from), Math.min(peaks.length, Math.max(from + 1, to)));
    }

    drawWave(frac) {
        if (!this.el.wave) return;
        const light = document.documentElement.getAttribute('data-theme') === 'light';
        drawPeaks(this.el.wave, this.curPeaks, frac, {
            played: light ? 'rgba(2,132,199,.95)' : 'rgba(56,189,248,.95)',
            rest: light ? 'rgba(100,116,139,.35)' : 'rgba(148,163,184,.30)',
        });
    }

    renderNow() {
        const tr = this.lib.current;
        const e = this.el;
        if (!tr) {
            e.title.textContent = t('nothing');
            e.sub.textContent = '';
            e.tech.textContent = '';
            e.coverImg.removeAttribute('src');
            e.cover.classList.remove('has-art');
            return;
        }
        const m = tr.meta || {};
        e.title.textContent = m.title || tr.name;
        e.sub.textContent = [m.artist, m.album].filter(Boolean).join(' — ');
        const bits = [];
        if (tr.ext) bits.push(tr.ext.toUpperCase());
        if (tr.fmt) {
            let f = (tr.fmt.sampleRate / 1000).toFixed(1).replace(/\.0$/, '') + ' ' + t('khz');
            if (tr.fmt.bits) f += '/' + tr.fmt.bits + ' ' + t('bit');
            if (tr.fmt.channels === 1) f += ' ' + t('mono');
            bits.push(f);
        }
        if (tr.size) bits.push((tr.size / 1048576).toFixed(1) + ' ' + t('mb'));
        if (tr.lufs != null) bits.push(tr.lufs.toFixed(1) + ' LUFS');
        e.tech.textContent = bits.join(' · ');
        // Частота ВЫХОДА живёт отдельной строкой в панели звука: смешивать её
        // со свойствами файла нельзя, это разные вещи.
        const outEl = $('mpOutRate');
        if (outEl && this.eng.ready) {
            const same = tr.fmt && tr.fmt.sampleRate === this.eng.sampleRate;
            outEl.textContent = t(same ? 'out_same' : 'out_resampled', {
                out: (this.eng.sampleRate / 1000).toFixed(1).replace(/\.0$/, ''),
                src: tr.fmt ? (tr.fmt.sampleRate / 1000).toFixed(1).replace(/\.0$/, '') : '?',
            });
        }
        if (tr.cover) {
            e.coverImg.src = tr.cover;
            e.cover.classList.add('has-art');
            // Размытая обложка подложкой пульта: цвет берётся из самой записи,
            // поэтому страница окрашивается под то, что звучит, и ни одна
            // палитра не выдумывается.
            $('mpDeck').style.setProperty('--mp-art', `url("${tr.cover}")`);
        } else {
            e.coverImg.removeAttribute('src');
            e.cover.classList.remove('has-art');
            $('mpDeck').style.removeProperty('--mp-art');
        }
        if (this.meters) this.meters.setLufs(tr.lufs, tr.rgSource);
    }

    updatePlayIcon() {
        const on = this.playing && this.eng.ready && !this.eng.el().paused;
        if (this.el.playIcon) this.el.playIcon.innerHTML = on
            ? '<rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/>'
            : '<path d="M6 4l14 8-14 8z"/>';
        this.el.play.setAttribute('aria-label', on ? t('pause') : t('play'));
        this.el.app.classList.toggle('is-playing', on);
    }

    /* ── список ───────────────────────────────────────────────────────── */

    updateCount() {
        const n = this.lib.tracks.length;
        const total = this.lib.tracks.reduce((a, x) => a + (x.duration || 0), 0);
        this.el.count.textContent = n ? t('count', { n, time: fmtTime(total) }) : '';
    }

    /* Пустой экран — это не «пульт без трека», а приглашение положить музыку.
       Замер первого вида: пульт занимал 497 пикселей пустых шкал, а
       единственная подсказка «перетащите файлы» лежала на 1263-м пикселе,
       то есть ниже экрана. Человек видел прибор и не видел, куда класть. */
    syncEmptyState() {
        const has = this.lib.tracks.length > 0;
        if (this.el.app) this.el.app.classList.toggle('is-empty', !has);
        if (this.el.hero) this.el.hero.hidden = has;
        if (this.el.deck) this.el.deck.hidden = !has;
        if (this.el.listPanel) this.el.listPanel.hidden = !has;
    }

    renderList() {
        const view = this.lib.view();
        const body = this.el.listBody;
        this.updateCount();
        this.syncEmptyState();
        this.el.empty.hidden = view.length > 0;
        if (this.el.queueBadge) {
            this.el.queueBadge.hidden = !this.lib.queue.length;
            this.el.queueBadge.textContent = t('in_queue', { n: this.lib.queue.length });
        }
        body.style.height = (view.length * this.rowH) + 'px';
        /* Прокрутка появляется только когда список ДЕЙСТВИТЕЛЬНО длинный.
           Коробка с полосой прокрутки вокруг одного трека — то, чего нет ни у
           одного приличного плеера. */
        this.el.list.style.maxHeight = (Math.min(view.length, 12) * this.rowH) + 'px';
        this.renderRows();
    }

    renderRows() {
        const view = this.lib.view();
        const vp = this.el.list, body = this.el.listBody;
        const top = vp.scrollTop, h = vp.clientHeight;
        const from = Math.max(0, Math.floor(top / this.rowH) - 4);
        const to = Math.min(view.length, Math.ceil((top + h) / this.rowH) + 4);
        let html = '';
        for (let i = from; i < to; i++) {
            const tr = view[i];
            const m = tr.meta || {};
            const cur = tr.id === this.lib.currentId;
            const qp = this.lib.queuePositionOf(tr.id);
            /* ⚠️ Ровно СЕМЬ детей, всегда. Сетка строки объявлена на семь
               колонок, и «необязательный» значок очереди делал восьмого
               ребёнка: он переносился на вторую строку сетки, строка
               становилась 64 пикселя вместо 46, и у плейлиста из ОДНОГО трека
               появлялась вертикальная прокрутка. Значок теперь всегда есть,
               просто пустой, а обе кнопки живут в одной ячейке. */
            html += `<div class="mp-row${cur ? ' is-current' : ''}" data-id="${tr.id}" data-i="${i}" draggable="true" style="top:${i * this.rowH}px">
                <span class="mp-row-n">${cur ? '<span class="mp-eq3"><i></i><i></i><i></i></span>' : (i + 1)}</span>
                <span class="mp-row-art">${tr.cover ? `<img src="${esc(tr.cover)}" alt="">` : NOTE_SVG}</span>
                <span class="mp-row-main"><b>${esc(m.title || tr.name)}</b><i>${esc([m.artist, m.album].filter(Boolean).join(' — ') || tr.ext.toUpperCase())}</i></span>
                <span class="mp-row-lufs">${tr.lufs != null ? tr.lufs.toFixed(1) : (tr.rgSource === 'tag' ? t('tag') : '')}</span>
                <span class="mp-row-dur">${tr.duration ? fmtTime(tr.duration) : '--:--'}</span>
                <span class="mp-row-q${qp ? '' : ' is-empty'}"${qp ? ` title="${esc(t('in_queue', { n: qp }))}"` : ''}>${qp || ''}</span>
                <span class="mp-row-act">
                    <button class="mp-row-btn" data-act="queue" title="${esc(t('queue_next'))}" aria-label="${esc(t('queue_next'))}"><svg viewBox="0 0 24 24"><path d="M4 6h11M4 12h11M4 18h7M17 14v7M20.5 17.5h-7"/></svg></button>
                    <button class="mp-row-btn" data-act="rm" title="${esc(t('remove'))}" aria-label="${esc(t('remove'))}"><svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18"/></svg></button>
                </span>
            </div>`;
        }
        body.innerHTML = html;
    }

    /* ── эквалайзер ───────────────────────────────────────────────────── */

    buildEqSliders() {
        const set = BAND_SETS[this.eng.bandCount];
        const g = this.eng.eqGains;
        let html = '';
        for (let i = 0; i < set.freqs.length; i++) {
            const f = set.freqs[i];
            const label = f >= 1000 ? (f / 1000).toString().replace('.0', '') + 'k' : String(f);
            html += `<div class="mp-band"><span class="mp-band-slot"><input type="range" class="mp-band-s" data-band="${i}" min="-12" max="12" step="0.5" value="${g[i] || 0}"
                aria-label="${label} ${t('hz')}" orient="vertical"></span><span class="mp-band-v" data-bv="${i}">${(g[i] || 0).toFixed(1)}</span><span class="mp-band-f">${label}</span></div>`;
        }
        this.el.eqBands.innerHTML = html;
        this.el.eqBands.classList.toggle('is-dense', this.eng.bandCount === 31);
        this.syncPreamp();
    }

    setBandGain(i, v) {
        const g = this.eng.eqGains.slice();
        g[i] = v;
        this.eng.setEqGains(g);
        const out = this.el.eqBands.querySelector(`[data-bv="${i}"]`);
        if (out) out.textContent = (v > 0 ? '+' : '') + v.toFixed(1);
        store.save('gains:' + this.eng.bandCount, g);
        store.save('preset', 'custom');
        this.el.eqPreset.value = 'custom';
        this.syncPreamp();
    }

    syncPreamp() {
        const auto = this.eng.preampMode === 'auto';
        const db = this.eng.currentPreampDb();
        if (this.el.eqPreampVal) this.el.eqPreampVal.textContent = (db > 0 ? '+' : '') + db.toFixed(1);
        if (this.el.eqPreamp) { this.el.eqPreamp.value = auto ? db.toFixed(1) : this.eng.manualPreampDb; this.el.eqPreamp.disabled = auto; }
        if (this.el.eqAuto) this.el.eqAuto.checked = auto;
    }

    /* ── параметрический эквалайзер ───────────────────────────────────
       Свободный набор фильтров вместо полос на фиксированных частотах.
       Именно этот режим принимает файлы AutoEQ под модель наушников —
       функция, за которую в соседнем классе берут деньги. */

    syncEqMode() {
        const param = this.eng.eqMode === 'parametric';
        if (this.el.param) this.el.param.hidden = !param;
        if (this.el.eqBands) this.el.eqBands.hidden = param;
        if (this.el.eqPreset) this.el.eqPreset.disabled = param;
        if (param) this.buildParamRows(); else this.buildEqSliders();
        this.syncPreamp();
    }

    buildParamRows() {
        const rows = this.el.paramRows;
        if (!rows) return;
        const fs = this.eng.filters;
        if (!fs.length) { rows.innerHTML = `<div class="mp-param-empty">${esc(t('pf_empty'))}</div>`; return; }
        const types = [['peaking', t('pf_pk')], ['lowshelf', t('pf_ls')], ['highshelf', t('pf_hs')]];
        rows.innerHTML = fs.map((f, i) => {
            const opts = types.map(([v, lbl]) =>
                `<option value="${v}"${f.type === v ? ' selected' : ''}>${esc(lbl)}</option>`).join('');
            // У шельфов Web Audio Q не использует (см. parametric.js) — поле
            // выключено, чтобы человек не крутил ручку, которая ничего не делает.
            const qOff = f.type !== 'peaking' ? ' disabled' : '';
            return `<div class="mp-param-row${f.on === false ? ' is-off' : ''}" data-f="${i}">
                <select data-pf="type" aria-label="${esc(t('pf_type'))}">${opts}</select>
                <label class="mp-param-cell"><span>${esc(t('pf_freq'))}</span><input type="number" data-pf="freq" value="${Math.round(f.freq)}" min="10" max="24000" step="1" aria-label="${esc(t('pf_freq'))}"></label>
                <label class="mp-param-cell"><span>${esc(t('pf_gain'))}</span><input type="number" data-pf="gain" value="${f.gain.toFixed(1)}" min="-24" max="24" step="0.1" aria-label="${esc(t('pf_gain'))}"></label>
                <label class="mp-param-cell"><span>${esc(t('pf_q'))}</span><input type="number" data-pf="q" value="${f.q.toFixed(2)}" min="0.1" max="20" step="0.05"${qOff} aria-label="${esc(t('pf_q'))}"></label>
                <button class="mp-param-del" data-pf="del" type="button" title="${esc(t('remove'))}" aria-label="${esc(t('remove'))}"><svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18"/></svg></button>
            </div>`;
        }).join('');
    }

    setFilterField(i, field, value) {
        const fs = this.eng.filters.map(f => Object.assign({}, f));
        if (!fs[i]) return;
        if (field === 'type') fs[i].type = value;
        else fs[i][field] = +value;
        const clean = makeFilter(fs[i].type, fs[i].freq, fs[i].gain, fs[i].q);
        clean.on = fs[i].on !== false;
        fs[i] = clean;
        this.eng.setFilters(fs);
        store.save('filters', fs);
        // Тип меняет доступность Q — строку надо перерисовать.
        if (field === 'type') this.buildParamRows();
        this.syncPreamp();
    }

    addFilter() {
        if (this.eng.filters.length >= MAX_FILTERS) {
            window.__toast && window.__toast(t('pf_too_many', { n: MAX_FILTERS }), 'warning');
            return;
        }
        const fs = this.eng.filters.concat([makeFilter('peaking', 1000, 0, 1)]);
        this.eng.setFilters(fs);
        store.save('filters', fs);
        this.buildParamRows();
        this.syncPreamp();
    }

    removeFilter(i) {
        const fs = this.eng.filters.filter((_, k) => k !== i);
        this.eng.setFilters(fs);
        store.save('filters', fs);
        this.buildParamRows();
        this.syncPreamp();
    }

    /**
     * Файл настроек AutoEQ / Equalizer APO.
     *
     * Формат определяется по содержимому: список фильтров уезжает в
     * параметрический режим, целевая кривая — в обычные полосы. Преамп из
     * файла НЕ берётся: свой считается по |H(f)| нашей цепочки (см.
     * parametric.js), иначе «загрузил файл под наушники» может кончиться
     * перегрузкой.
     */
    async importEqText(text, name) {
        await this.ensureEngine();
        const r = parseEqText(text);
        if (r.kind === 'parametric') {
            this.eng.setFilters(r.filters);
            this.eng.setEqMode('parametric');
            this.eng.setEqEnabled(true);
            this.el.eqOn.checked = true;
            this.el.eqBandCount.value = 'param';
            store.save('filters', r.filters);
            store.save('bands', 'param');
            store.save('eqOn', true);
            this.syncEqMode();
            const note = this.el.paramNote;
            if (note) {
                if (r.skipped && r.skipped.length) {
                    // Молчать здесь нельзя: кривая применена НЕ целиком.
                    note.textContent = t('pf_skipped', { n: r.skipped.length, types: [...new Set(r.skipped)].join(', ') });
                    note.classList.add('is-warn');
                } else {
                    note.textContent = t('pf_loaded', { n: r.filters.length, name: name || '' });
                    note.classList.remove('is-warn');
                }
            }
            window.__toast && window.__toast(t('pf_loaded', { n: r.filters.length, name: name || '' }), 'success');
            return true;
        }
        if (r.kind === 'graphic') {
            const n = this.eng.bandCount;
            const gains = BAND_SETS[n].freqs.map(f => Math.round(curveAt(r.curve, f) * 10) / 10);
            this.eng.setEqMode('graphic');
            this.eng.setEqGains(gains);
            this.eng.setEqEnabled(true);
            this.el.eqOn.checked = true;
            this.el.eqBandCount.value = String(n);
            this.el.eqPreset.value = 'custom';
            store.save('gains:' + n, gains);
            store.save('preset', 'custom');
            store.save('bands', n);
            store.save('eqOn', true);
            this.syncEqMode();
            window.__toast && window.__toast(t('pf_curve_loaded', { n: r.curve.length }), 'success');
            return true;
        }
        window.__toast && window.__toast(t('pf_unreadable'), 'error');
        return false;
    }

    exportEq() {
        // Забрать настройку к себе — обмен в одну сторону это ловушка.
        const fs = this.eng.eqMode === 'parametric'
            ? this.eng.filters
            : BAND_SETS[this.eng.bandCount].freqs.map((f, i) =>
                makeFilter('peaking', f, this.eng.eqGains[i] || 0, BAND_SETS[this.eng.bandCount].q));
        const text = exportEqText(fs, this.eng.ctx ? this.eng.ctx.sampleRate : 48000);
        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
        window.__downloadFile(blob, 'timbrica-eq.txt');
    }

    applyPreset(name) {
        if (name === 'custom') return;
        const g = presetGains(name, this.eng.bandCount);
        this.eng.setEqGains(g);
        store.save('gains:' + this.eng.bandCount, g);
        store.save('preset', name);
        this.buildEqSliders();
    }

    /* ── устройства вывода ────────────────────────────────────────────── */

    async fillDevices() {
        const sel = this.el.device;
        if (!sel || !this.eng.ctx || typeof this.eng.ctx.setSinkId !== 'function') {
            if (this.el.deviceWrap) this.el.deviceWrap.hidden = true;
            return;
        }
        try {
            const list = await navigator.mediaDevices.enumerateDevices();
            const outs = list.filter(d => d.kind === 'audiooutput');
            if (outs.length < 2) { if (this.el.deviceWrap) this.el.deviceWrap.hidden = true; return; }
            sel.innerHTML = outs.map((d, i) =>
                `<option value="${esc(d.deviceId)}">${esc(d.label || t('device_n', { n: i + 1 }))}</option>`).join('');
            this.el.deviceWrap.hidden = false;
        } catch (_) {
            if (this.el.deviceWrap) this.el.deviceWrap.hidden = true;
        }
    }

    /* ── таймер сна ───────────────────────────────────────────────────── */

    setSleep(min) {
        this.clearSleep();
        if (!min) return;
        this.sleepAt = Date.now() + min * 60000;
        this.sleepTimer = setInterval(() => this.tickSleep(), 500);
        this.el.sleep.classList.add('is-active');
        this.el.sleepPop.hidden = true;
        window.__toast && window.__toast(t('sleep_set_to', { n: min }), 'info');
    }

    clearSleep() {
        if (this.sleepTimer) clearInterval(this.sleepTimer);
        this.sleepTimer = 0; this.sleepAt = 0;
        this.el.sleep.classList.remove('is-active');
        this.el.sleepLabel.textContent = t('sleep');
        if (this.eng.ready) this.eng.setVolume(this.savedVol);
    }

    tickSleep() {
        const left = this.sleepAt - Date.now();
        if (left <= 0) { this.stop(); this.clearSleep(); return; }
        const s = Math.ceil(left / 1000);
        this.el.sleepLabel.textContent = Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');
        // Последние 30 секунд — плавное затухание, чтобы музыка не обрывалась.
        if (left < 30000 && this.eng.ready) this.eng.setVolume(this.savedVol * (left / 30000));
    }

    syncStopAfter() {
        if (this.el.stopAfter) this.el.stopAfter.classList.toggle('is-active', this.stopAfterCurrent);
    }

    /* ── системная панель ─────────────────────────────────────────────── */

    mediaSession(tr) {
        if (!('mediaSession' in navigator) || !tr) return;
        const m = tr.meta || {};
        try {
            const art = tr.cover ? [{ src: tr.cover, sizes: '512x512', type: 'image/jpeg' }] : [];
            navigator.mediaSession.metadata = new window.MediaMetadata({
                title: m.title || tr.name, artist: m.artist || '', album: m.album || '', artwork: art,
            });
            navigator.mediaSession.playbackState = this.playing ? 'playing' : 'paused';
            const set = (a, fn) => { try { navigator.mediaSession.setActionHandler(a, fn); } catch (_) {} };
            set('play', () => this.togglePlay());
            set('pause', () => this.togglePlay());
            set('previoustrack', () => this.prev());
            set('nexttrack', () => this.next(true));
            set('seekbackward', () => { const e = this.eng.el(); e.currentTime = Math.max(0, e.currentTime - 10); });
            set('seekforward', () => { const e = this.eng.el(); e.currentTime = Math.min(e.duration || 0, e.currentTime + 10); });
            set('seekto', (d) => { if (d && d.seekTime != null) this.eng.el().currentTime = d.seekTime; });
            set('stop', () => this.stop());
        } catch (_) {}
    }

    /* ── события интерфейса ───────────────────────────────────────────── */

    bindUi() {
        const e = this.el;
        const stop = (ev) => ev.stopPropagation();

        /* Общий примитив, а не сырой addEventListener: плеер грузится модульным
           графом, то есть ОТЛОЖЕННО, и на медленной сети человек успевает
           выбрать файл раньше, чем модуль подпишется. Сырой слушатель такое
           событие теряет насовсем — тул стоит мёртвым до повторного выбора
           (класс закрыт после жалобы 08.05.2026). Примитив ещё и доигрывает
           уже выбранный файл. Список берём из detail.files: плеер многофайловый,
           а в колбэк приходит только первый. */
        const takeFiles = (f, detail) => {
            const files = (detail && detail.files && detail.files.length) ? detail.files : (f ? [f] : []);
            if (files.length) this.addFiles(files);
        };
        if (typeof window.__bindUploaderFile === 'function') window.__bindUploaderFile(e.app, takeFiles);
        else e.app.addEventListener('tool-uploader:file', (ev) => takeFiles(ev.detail && ev.detail.file, ev.detail));
        const openFiles = (ev) => { stop(ev); e.app.__uploaderOpen && e.app.__uploaderOpen(); };
        $('mpAdd').addEventListener('click', openFiles);
        if ($('mpHeroFiles')) $('mpHeroFiles').addEventListener('click', openFiles);
        if ($('mpHeroFolder')) $('mpHeroFolder').addEventListener('click', (ev) => { stop(ev); $('mpAddFolder').click(); });
        $('mpAddFolder').addEventListener('click', async (ev) => {
            stop(ev);
            /* Выбор КАТАЛОГА через File System Access, если браузер умеет: он
               даёт дескриптор, который можно запомнить, и в следующий раз
               фонотека откроется одним нажатием. webkitdirectory такого не
               даёт вовсе — только разовый список файлов, поэтому он остаётся
               запасным путём для Firefox и Safari. */
            if (Store.canPersist()) {
                const picked = await Store.pickFolder();
                if (picked) { this.folderName = picked.name; await this.addFiles(picked.files, { folder: picked.name }); return; }
                return;
            }
            $('mpFolderInput').click();
        });
        $('mpFolderInput').addEventListener('change', (ev) => { this.addFiles(ev.target.files); ev.target.value = ''; });

        e.play.addEventListener('click', (ev) => { stop(ev); this.togglePlay(); });
        e.next.addEventListener('click', (ev) => { stop(ev); this.next(true); });
        e.prev.addEventListener('click', (ev) => { stop(ev); this.prev(); });
        e.back.addEventListener('click', (ev) => { stop(ev); if (this.eng.ready) { const x = this.eng.el(); x.currentTime = Math.max(0, x.currentTime - 10); } });
        e.fwd.addEventListener('click', (ev) => { stop(ev); if (this.eng.ready) { const x = this.eng.el(); x.currentTime = Math.min(x.duration || 0, x.currentTime + 10); } });

        e.shuffle.addEventListener('click', (ev) => {
            stop(ev);
            this.lib.setShuffle(!this.lib.shuffle);
            store.save('shuffle', this.lib.shuffle);
            e.shuffle.classList.toggle('is-active', this.lib.shuffle);
            e.shuffle.setAttribute('aria-pressed', String(this.lib.shuffle));
            this.cancelHandoff();
        });
        e.repeat.addEventListener('click', (ev) => {
            stop(ev);
            this.lib.repeat = { off: 'all', all: 'one', one: 'off' }[this.lib.repeat];
            store.save('repeat', this.lib.repeat);
            this.syncRepeat();
            this.cancelHandoff();
        });

        // Перемотка мышью и пальцем по всей ширине волны.
        const seekFrom = (ev) => {
            const r = e.waveWrap.getBoundingClientRect();
            const x = (ev.touches ? ev.touches[0].clientX : ev.clientX) - r.left;
            this.seekTo(Math.max(0, Math.min(1, x / r.width)));
        };
        let dragging = false;
        e.waveWrap.addEventListener('pointerdown', (ev) => { stop(ev); dragging = true; e.waveWrap.setPointerCapture(ev.pointerId); seekFrom(ev); });
        e.waveWrap.addEventListener('pointermove', (ev) => { if (dragging) seekFrom(ev); });
        e.waveWrap.addEventListener('pointerup', () => { dragging = false; });
        e.waveWrap.addEventListener('keydown', (ev) => {
            if (!this.eng.ready) return;
            const x = this.eng.el();
            if (ev.key === 'ArrowRight') { x.currentTime = Math.min(x.duration || 0, x.currentTime + 5); ev.preventDefault(); }
            if (ev.key === 'ArrowLeft') { x.currentTime = Math.max(0, x.currentTime - 5); ev.preventDefault(); }
        });

        e.vol.addEventListener('input', () => {
            this.savedVol = +e.vol.value;
            store.save('volume', this.savedVol);
            if (this.eng.ready) { this.eng.setMuted(false); this.eng.setVolume(this.savedVol); }
            this.syncVolIcon();
        });
        e.mute.addEventListener('click', (ev) => {
            stop(ev);
            if (!this.eng.ready) return;
            this.eng.setMuted(!this.eng.muted);
            this.syncVolIcon();
        });
        e.rate.addEventListener('change', () => {
            this.savedRate = +e.rate.value;
            store.save('rate', this.savedRate);
            if (this.eng.ready) for (const x of this.eng.els) x.playbackRate = this.savedRate;
        });

        e.search.addEventListener('input', () => { this.lib.setFilter(e.search.value); this.renderList(); });
        e.list.addEventListener('scroll', () => this.renderRows());
        e.list.addEventListener('click', (ev) => {
            const row = ev.target.closest('.mp-row');
            if (!row) return;
            stop(ev);
            const id = +row.dataset.id;
            const act = ev.target.closest('[data-act]');
            if (!act) { this.playTrack(id); return; }
            if (act.dataset.act === 'rm') { this.remove(id); return; }
            if (act.dataset.act === 'queue') {
                if (this.lib.queuePositionOf(id)) this.lib.dequeue(id); else this.lib.enqueue(id);
                this.cancelHandoff();
                this.renderList();
            }
        });
        // Перетаскивание строк — только когда порядок «как добавлено».
        let dragId = null;
        e.list.addEventListener('dragstart', (ev) => {
            const row = ev.target.closest('.mp-row'); if (!row) return;
            dragId = +row.dataset.i; ev.dataTransfer.effectAllowed = 'move';
        });
        e.list.addEventListener('dragover', (ev) => { if (dragId != null) ev.preventDefault(); });
        e.list.addEventListener('drop', (ev) => {
            const row = ev.target.closest('.mp-row');
            if (dragId == null || !row) return;
            ev.preventDefault();
            if (!this.lib.move(dragId, +row.dataset.i)) window.__toast && window.__toast(t('reorder_needs_added'), 'info');
            dragId = null;
            this.renderList();
        });

        $('mpClear').addEventListener('click', (ev) => {
            stop(ev);
            this.stop();
            for (const u of this.blobUrls.values()) { try { URL.revokeObjectURL(u); } catch (_) {} }
            this.blobUrls.clear();
            this.lib.clear();
            this.curPeaks = null;
            this.renderList(); this.renderNow(); this.drawWave(0);
        });
        $('mpExport').addEventListener('click', (ev) => {
            stop(ev);
            if (!this.lib.tracks.length) return;
            const blob = new Blob([this.lib.toM3U()], { type: 'audio/x-mpegurl' });
            window.__downloadFile(blob, 'playlist.m3u8');
        });

        for (const th of document.querySelectorAll('[data-sort]')) {
            th.addEventListener('click', (ev) => { stop(ev); this.lib.setSort(th.dataset.sort); this.renderList(); this.syncSort(); });
        }

        // Эквалайзер
        $('mpEqToggle').addEventListener('click', (ev) => {
            stop(ev);
            const open = e.eqPanel.hasAttribute('hidden');
            e.eqPanel.toggleAttribute('hidden', !open);
            $('mpEqToggle').classList.toggle('is-active', open);
            $('mpEqToggle').setAttribute('aria-expanded', String(open));
            if (this.spectrum) this.spectrum.showCurve = open;
        });
        e.eqBands.addEventListener('input', (ev) => {
            const s = ev.target.closest('[data-band]');
            if (s) this.setBandGain(+s.dataset.band, +s.value);
        });
        // Плотная сетка (31): числа под колонками невидимы — значение показывает
        // одна плавающая подпись над активной полосой (общий band-readout.js,
        // тот же у /equalizer). После слушателя input — читает новое значение.
        if (window.__bandReadout) {
            window.__bandReadout.attach(e.eqBands, {
                band: '.mp-band', slider: '.mp-band-s', label: '.mp-band-v',
                text: (s, band) => {
                    const f = band.querySelector('.mp-band-f');
                    const v = +s.value || 0;
                    return (f ? f.textContent + ' ' + t('hz') + '  ' : '') + (v > 0 ? '+' : '') + v.toFixed(1) + ' dB';
                },
                active: () => !!this.eng && this.eng.bandCount === 31,
            });
        }
        e.eqOn.addEventListener('change', async () => {
            await this.ensureEngine();
            this.eng.setEqEnabled(e.eqOn.checked);
            store.save('eqOn', e.eqOn.checked);
            this.syncPreamp();
        });
        e.eqBandCount.addEventListener('change', async () => {
            await this.ensureEngine();
            const v = e.eqBandCount.value;
            if (v === 'param') {
                this.eng.setEqMode('parametric');
                store.save('bands', 'param');
            } else {
                const n = +v;
                this.eng.setEqMode('graphic');
                this.eng.setBandCount(n);
                const saved = store.load('gains:' + n, new Array(BAND_SETS[n].freqs.length).fill(0));
                this.eng.setEqGains(saved);
                store.save('bands', n);
            }
            this.syncEqMode();
        });

        if (e.paramAdd) e.paramAdd.addEventListener('click', async (ev) => {
            stop(ev); await this.ensureEngine(); this.addFilter();
        });
        if (e.paramRows) {
            const onField = async (ev) => {
                const row = ev.target.closest('.mp-param-row');
                const what = ev.target.dataset ? ev.target.dataset.pf : null;
                if (!row || !what || what === 'del') return;
                await this.ensureEngine();
                this.setFilterField(+row.dataset.f, what, ev.target.value);
            };
            e.paramRows.addEventListener('input', onField);
            e.paramRows.addEventListener('change', onField);
            e.paramRows.addEventListener('click', async (ev) => {
                const btn = ev.target.closest('[data-pf="del"]');
                if (!btn) return;
                stop(ev); await this.ensureEngine();
                this.removeFilter(+btn.closest('.mp-param-row').dataset.f);
            });
        }
        if (e.eqImport) e.eqImport.addEventListener('click', (ev) => { stop(ev); e.eqFile.click(); });
        if (e.eqFile) e.eqFile.addEventListener('change', async () => {
            const f = e.eqFile.files && e.eqFile.files[0];
            if (!f) return;
            try { await this.importEqText(await f.text(), f.name); }
            catch (err) { window.__handleError && window.__handleError(err, 'eq-import'); }
            e.eqFile.value = '';
        });
        if (e.eqExport) e.eqExport.addEventListener('click', async (ev) => {
            stop(ev); await this.ensureEngine(); this.exportEq();
        });
        e.eqPreset.addEventListener('change', async () => { await this.ensureEngine(); this.applyPreset(e.eqPreset.value); });
        e.eqAuto.addEventListener('change', async () => {
            await this.ensureEngine();
            this.eng.setPreamp(e.eqAuto.checked ? 'auto' : 'manual', +e.eqPreamp.value);
            store.save('preampAuto', e.eqAuto.checked);
            this.syncPreamp();
        });
        e.eqPreamp.addEventListener('input', async () => {
            await this.ensureEngine();
            this.eng.setPreamp('manual', +e.eqPreamp.value);
            this.syncPreamp();
        });
        e.eqReset.addEventListener('click', async (ev) => { stop(ev); await this.ensureEngine(); this.applyPreset('flat'); e.eqPreset.value = 'flat'; });

        e.rgMode.addEventListener('change', async () => {
            await this.ensureEngine();
            this.eng.setRgMode(e.rgMode.value);
            store.save('rg', e.rgMode.value);
            const tr = this.lib.current;
            if (tr) { this.applyGain(this.eng.active, tr); this.ensureGain(tr); }
        });
        e.rgScan.addEventListener('click', async (ev) => {
            stop(ev);
            await this.ensureEngine();
            for (const tr of this.lib.tracks) if (tr.lufs == null && !this.scanQueue.includes(tr.id)) this.scanQueue.push(tr.id);
            window.__toast && window.__toast(t('rg_scan_started', { n: this.scanQueue.length }), 'info');
            this.drainScanQueue();
        });
        e.limiter.addEventListener('change', async () => {
            await this.ensureEngine();
            this.eng.setLimiter(e.limiter.checked);
            store.save('limiter', e.limiter.checked);
        });
        e.device.addEventListener('change', async () => { await this.eng.setOutputDevice(e.device.value); });
        if (e.savedOpen) e.savedOpen.addEventListener('click', (ev) => { stop(ev); this.reopenSaved(); });
        if (e.savedForget) e.savedForget.addEventListener('click', async (ev) => {
            stop(ev); await Store.forget(); this.savedIndex = null; e.saved.hidden = true;
        });
        /* Свёртка: коррекция наушников (файл из AutoEQ под свою модель) или
           комнаты (импульс из REW). Чужие измерения мы не раздаём — человек
           приносит файл сам. */
        e.convPick.addEventListener('click', (ev) => { stop(ev); e.convInput.click(); });
        e.convInput.addEventListener('change', async (ev) => {
            const f = ev.target.files && ev.target.files[0];
            ev.target.value = '';
            if (!f) return;
            await this.ensureEngine();
            const info = await this.eng.loadImpulseFile(f);
            if (info.error) {
                const msg = info.error === 'too-long'
                    ? t('conv_err_long', { s: String(Math.round(info.seconds || 0)) })
                    : t('conv_err_decode');
                window.__toast && window.__toast(msg, 'warning');
                return;
            }
            e.convOnWrap.hidden = false;
            e.convClear.hidden = false;
            e.convOn.checked = true;
            this.eng.setConvolution(true);
            e.convInfo.textContent = t('conv_loaded', {
                name: f.name,
                taps: t('conv_taps', { n: String(info.taps) }),
                trim: (this.eng.conv.trimDb || 0).toFixed(1),
            });
        });
        e.convOn.addEventListener('change', () => { this.eng.setConvolution(e.convOn.checked); });
        e.convClear.addEventListener('click', (ev) => {
            stop(ev);
            this.eng.clearImpulse();
            e.convOnWrap.hidden = true;
            e.convClear.hidden = true;
            e.convInfo.textContent = '';
        });

        e.crossfeed.addEventListener('change', async () => {
            await this.ensureEngine();
            this.savedCrossfeed = e.crossfeed.value;
            this.eng.setCrossfeed(this.savedCrossfeed);
            store.save('crossfeed', this.savedCrossfeed);
        });
        e.crossfade.addEventListener('input', () => {
            this.crossfade = +e.crossfade.value;
            store.save('crossfade', this.crossfade);
            e.crossfadeVal.textContent = this.crossfade ? t('sec_n', { n: this.crossfade }) : t('gapless');
            this.cancelHandoff();
        });

        e.sleep.addEventListener('click', (ev) => { stop(ev); e.sleepPop.hidden = !e.sleepPop.hidden; });
        e.sleepPop.addEventListener('click', (ev) => {
            stop(ev);
            const b = ev.target.closest('[data-min]');
            if (b) this.setSleep(+b.dataset.min);
        });
        document.addEventListener('click', (ev) => {
            if (!ev.target.closest('#mpSleep') && !ev.target.closest('#mpSleepPop')) e.sleepPop.hidden = true;
        });
        e.stopAfter.addEventListener('click', (ev) => {
            stop(ev);
            this.stopAfterCurrent = !this.stopAfterCurrent;
            this.syncStopAfter();
            if (this.stopAfterCurrent) this.cancelHandoff();
        });

        window.addEventListener('resize', () => { if (this.curPeaks) this.drawWave(this.lastFrac || 0); });

        // Начальные положения органов управления
        e.vol.value = this.savedVol;
        e.rate.value = String(this.savedRate);
        e.eqOn.checked = this.savedEqOn;
        e.eqBandCount.value = this.savedEqMode === 'parametric' ? 'param' : String(this.savedBands);
        // Пока звук не поднят, движок ещё не знает про режим — рисуем по сохранённому.
        if (this.savedEqMode === 'parametric' && e.param) {
            e.param.hidden = false;
            if (e.eqBands) e.eqBands.hidden = true;
            if (e.eqPreset) e.eqPreset.disabled = true;
            /* Движок ещё не поднят (звук стартует по жесту), поэтому цепочку
               узлов не трогаем — только кладём фильтры в поле и рисуем их.
               setFilters() здесь звать нельзя: он полез бы в ctx, которого нет. */
            this.eng.filters = this.savedFilters.map(f => Object.assign({}, f));
            this.buildParamRows();
        }
        e.eqPreset.value = this.savedPreset;
        e.rgMode.value = this.savedRg;
        e.limiter.checked = this.savedLimiter;
        e.crossfeed.value = this.savedCrossfeed;
        e.crossfade.value = this.crossfade;
        e.crossfadeVal.textContent = this.crossfade ? t('sec_n', { n: this.crossfade }) : t('gapless');
        e.shuffle.classList.toggle('is-active', this.lib.shuffle);
        this.syncRepeat(); this.syncVolIcon(); this.syncSort();
    }

    remove(id) {
        const wasCur = this.lib.currentId === id;
        const u = this.blobUrls.get(id);
        if (u) { try { URL.revokeObjectURL(u); } catch (_) {} this.blobUrls.delete(id); }
        this.lib.remove(id);
        this.cancelHandoff();
        if (wasCur) { this.stop(); this.renderNow(); }
        this.renderList();
    }

    syncRepeat() {
        const m = this.lib.repeat;
        this.el.repeat.classList.toggle('is-active', m !== 'off');
        this.el.repeat.dataset.mode = m;
        this.el.repeat.setAttribute('aria-label', t('repeat_' + m));
        this.el.repeat.title = t('repeat_' + m);
    }

    syncVolIcon() {
        const v = this.eng.ready && this.eng.muted ? 0 : this.savedVol;
        this.el.mute.dataset.level = v === 0 ? 'off' : v < 0.5 ? 'low' : 'high';
        this.el.mute.setAttribute('aria-label', v === 0 ? t('unmute') : t('mute'));
    }

    syncSort() {
        for (const th of document.querySelectorAll('[data-sort]')) {
            const on = th.dataset.sort === this.lib.sort.by;
            th.dataset.dir = on ? (this.lib.sort.dir > 0 ? 'asc' : 'desc') : '';
            th.setAttribute('aria-sort', on ? (this.lib.sort.dir > 0 ? 'ascending' : 'descending') : 'none');
        }
    }

    /* ── клавиатура ───────────────────────────────────────────────────── */

    bindKeys() {
        document.addEventListener('keydown', (ev) => {
            const tag = (ev.target.tagName || '').toLowerCase();
            if (tag === 'input' || tag === 'textarea' || tag === 'select' || ev.target.isContentEditable) {
                if (ev.key === 'Escape') ev.target.blur();
                return;
            }
            if (ev.ctrlKey || ev.metaKey || ev.altKey) return;
            const el = this.eng.ready ? this.eng.el() : null;
            const K = {
                ' ': () => this.togglePlay(),
                k: () => this.togglePlay(),
                ArrowRight: () => { if (el) el.currentTime = Math.min(el.duration || 0, el.currentTime + 5); },
                ArrowLeft: () => { if (el) el.currentTime = Math.max(0, el.currentTime - 5); },
                ArrowUp: () => { this.el.vol.value = Math.min(1, this.savedVol + 0.05); this.el.vol.dispatchEvent(new Event('input')); },
                ArrowDown: () => { this.el.vol.value = Math.max(0, this.savedVol - 0.05); this.el.vol.dispatchEvent(new Event('input')); },
                n: () => this.next(true),
                p: () => this.prev(),
                m: () => this.el.mute.click(),
                s: () => this.el.shuffle.click(),
                r: () => this.el.repeat.click(),
                e: () => $('mpEqToggle').click(),
                '/': () => { this.el.search.focus(); },
            };
            const fn = K[ev.key];
            if (!fn) return;
            ev.preventDefault();
            fn();
        });
    }
}

const player = new Player();
window.__mp = player;                    // точка входа для тестов и отладки
player.boot();
